import { faFileImport, faTag, faTrashCan, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AlertModal,
  Checkbox,
  Flex,
  Popover,
  PopoverContent,
  PopperTrigger,
  Text,
  TextWithIcon,
  Toast,
  Tooltip,
  useOpenModal,
} from '@modusign/design-system';
import { useDeleteWorkspaceExternalDocuments } from '@modusign/openapi-client-external-document';
import { ExternalDocument } from '@modusign/openapi-client-external-document/generated/model';
import { useCallback, useState } from 'react';
import { useToggle } from 'react-use';

import BatchActionToolbar from '@app/components/BatchActionToolbar';
import ExternalDocumentBatchPermissionGrantModal from '@app/features/externalDocument/components/ExternalDocumentBatchPermissionGrantModal';
import useUpdateExternalDocumentLabels from '@app/features/externalDocument/hooks/useUpdateExternalDocumentLabels';
import LabelApplyModalPopover from '@app/features/label/components/LabelPopovers/LabelApplyPopover';
import { getLabelsLabelingState } from '@app/features/label/libs/getLabelsLabelingState';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentUserRole, useHasPublicWorkspace } from '@app/hooks/workspace';

import DeleteExternalDocumentModal from '../DeleteExternalDocumentModal';
import ExternalDocumenttransferModal from '../ExternalDocumentTransferModal';

interface DocumentBatchActionBarProps {
  documentList: ExternalDocument[];
  selectedDocumentIds: string[];
  onCheckboxClick: () => void;
  onComplete: (needRefetch: boolean) => void;
}

export default function DocumentBatchActionBar({
  documentList,
  selectedDocumentIds,
  onCheckboxClick,
  onComplete,
}: DocumentBatchActionBarProps) {
  const openModal = useOpenModal();
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();
  const hasPublicWorkspace = useHasPublicWorkspace();
  const role = useCurrentUserRole();

  const { isPermitted } = useMemberPermissions('ASSIGN_EXTERNAL_DOCUMENT');

  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false);
  const { mutate: archiveExternalDocuments, isLoading: isArchiving } =
    useDeleteWorkspaceExternalDocuments({
      mutation: {
        onError: createErrorHandler({ title: '외부문서 삭제' }),
        onSuccess: (result) => {
          if (result.error === 0) {
            Toast.success({
              description: `${result.results.length}개의 계약을 삭제했습니다.`,
            });
          } else if (result.error === result.results.length) {
            openModal(
              (props) => (
                <AlertModal {...props} maxWidth="xs" title="계약 삭제 안내" variant="warning">
                  <Text>삭제할 수 있는 계약이 없습니다.</Text>
                  <Text
                    as="div"
                    bg="boxBackgroundNeutralBase"
                    px={6}
                    py={7}
                    sx={{
                      mt: 6,
                      border: '1px solid',
                      borderColor: 'boxBorderNeutralBase',
                    }}
                  >
                    이미 삭제되었거나, 삭제 또는 접근 권한이 없는 계약은 삭제되지 않습니다.
                  </Text>
                </AlertModal>
              ),
              { keepOnUnmount: true }
            );
          } else {
            openModal(
              (props) => (
                <AlertModal {...props} maxWidth="xs" title="계약 삭제 완료" variant="completed">
                  <Text>{result.results.length - result.error}개의 계약을 삭제했습니다.</Text>
                  <Text
                    as="div"
                    bg="boxBackgroundNeutralBase"
                    px={6}
                    py={7}
                    sx={{
                      mt: 6,
                      border: '1px solid',
                      borderColor: 'boxBorderNeutralBase',
                    }}
                  >
                    이미 삭제되었거나, 삭제 또는 접근 권한이 없는 계약은 삭제되지 않습니다.
                  </Text>
                </AlertModal>
              ),
              { keepOnUnmount: true }
            );
          }
          toggleDeleteModal(false);
          onComplete(true);
        },
      },
    });

  const [isApplyLabelPopoverOpen, setApplyLabelPopoverOpen] = useState(false);
  const { mutateAsync } = useUpdateExternalDocumentLabels({ onSuccess: () => onComplete(true) });

  const selectedDocuments = documentList.filter((d) => selectedDocumentIds.includes(d.id));

  const handleOpenDocumentBatchPermissionGrantModal = useCallback(() => {
    openModal((props) => (
      <ExternalDocumentBatchPermissionGrantModal documents={selectedDocuments} {...props} />
    ));
  }, [openModal, selectedDocuments]);

  if (selectedDocumentIds.length === 0) {
    return null;
  }

  return (
    <BatchActionToolbar
      checkbox={
        <Checkbox
          checked={selectedDocumentIds.length === documentList.length}
          indeterminate={selectedDocumentIds.length !== documentList.length}
          onClick={onCheckboxClick}
        />
      }
      selectedItemsCount={selectedDocumentIds.length}
    >
      <Flex flex="auto" gap={4}>
        <Popover
          isOpen={isApplyLabelPopoverOpen}
          mode="click"
          onOpenChange={setApplyLabelPopoverOpen}
        >
          <Tooltip content="권한 없음" disabled={isPermitted}>
            <BatchActionToolbar.Button
              disabled={!isPermitted}
              leftIcon={faUserPlus}
              onClick={handleOpenDocumentBatchPermissionGrantModal}
            >
              일괄 공유
            </BatchActionToolbar.Button>
          </Tooltip>
          <PopperTrigger>
            <BatchActionToolbar.Button>
              <TextWithIcon iconContainerWidth="14px" leftIcon={<FontAwesomeIcon icon={faTag} />}>
                라벨
              </TextWithIcon>
            </BatchActionToolbar.Button>
          </PopperTrigger>
          <PopoverContent>
            <LabelApplyModalPopover
              initialLabeled={getLabelsLabelingState(selectedDocuments)}
              onSubmit={(data) => {
                return mutateAsync({
                  workspaceId,
                  data: {
                    documentLabels: data.flatMap((d) =>
                      selectedDocumentIds.map((documentId) => ({
                        documentId,
                        labelId: d.labelId,
                        apply: d.apply,
                      }))
                    ),
                  },
                });
              }}
            />
          </PopoverContent>
        </Popover>
        {/* [QA-895](https://modusign.atlassian.net/browse/QA-895)에서 공유 버튼을 제거했습니다. 나중에 다시 구현할 때 연관된 PR(#8955)을 revert해주세요. */}
        <BatchActionToolbar.Button onClick={toggleDeleteModal}>
          <TextWithIcon iconContainerWidth="14px" leftIcon={<FontAwesomeIcon icon={faTrashCan} />}>
            삭제
            <DeleteExternalDocumentModal
              context="BATCH_ACTION"
              documents={selectedDocuments}
              isLoading={isArchiving}
              isOpen={isDeleteModalOpen}
              onCancel={toggleDeleteModal}
              onConfirm={() => {
                archiveExternalDocuments({
                  workspaceId,
                  data: {
                    documentIds: selectedDocumentIds,
                  },
                });
              }}
            />
          </TextWithIcon>
        </BatchActionToolbar.Button>
      </Flex>
      {hasPublicWorkspace && role?.type === 'ADMIN' && (
        <BatchActionToolbar.Button
          mr={2}
          onClick={() => {
            openModal(({ onClose }) => (
              <ExternalDocumenttransferModal
                documents={selectedDocuments}
                onCancel={onClose}
                onComplete={() => {
                  onClose();
                  onComplete(true);
                }}
              />
            ));
          }}
        >
          <TextWithIcon
            iconContainerWidth="14px"
            leftIcon={<FontAwesomeIcon icon={faFileImport} />}
          >
            이전
          </TextWithIcon>
        </BatchActionToolbar.Button>
      )}
    </BatchActionToolbar>
  );
}
